import React, { MouseEvent } from "react";
import classNames from "classnames";
import { isFunction } from "@common/helpers/typeGuards";
import { ChildrenProps } from "../../../types";
import gridTable from "./GridTableBase.module.scss";
import styles from "./GridTable.module.scss";

interface GridTableProps {
    children: React.ReactNode;
    className?: string;
    highlightRowOnHover?: boolean;
    noBorder?: boolean;
    noRowBorder?: boolean;
    fullWidth?: boolean;
    gridTemplateColumns?: string;
}

/** @deprecated Use @design-system/ViewTable/ViewTable for product tables. GridTable is an internal layout primitive — do not import directly. */
export const GridTable = ({
    children,
    className,
    highlightRowOnHover,
    noBorder,
    noRowBorder,
    fullWidth,
    gridTemplateColumns,
}: GridTableProps) => (
    <section
        className={classNames(
            styles.container,
            gridTable.gridTable,
            className,
            {
                [gridTable.highlightRowOnHover]: highlightRowOnHover,
                [gridTable.noBorder]: noBorder,
                [gridTable.noRowBorder]: noRowBorder,
                [gridTable.fullWidth]: fullWidth,
            },
        )}
        style={{ gridTemplateColumns }}
    >
        {children}
    </section>
);

interface GridTableHeaderProps
    extends ChildrenProps, React.HTMLAttributes<HTMLDivElement> {
    columns?: Array<{
        key: string;
        label: string | React.ReactNode;
    }>;
    columnBorders?: boolean;
    className?: string;
}

/** @deprecated Use @design-system/ViewTable/ViewTable for product tables. GridTable is an internal layout primitive — do not import directly. */
export const GridTableHeader: React.FC<GridTableHeaderProps> = ({
    columns,
    columnBorders,
    className,
    children,
    ...divProps
}) => (
    <div
        {...divProps}
        className={classNames(styles.row, gridTable.headerRow, className, {
            [gridTable.columnBorders]: columnBorders,
        })}
    >
        {columns?.map((column) => (
            <div key={column.key}>{column.label}</div>
        ))}
        {children}
    </div>
);

interface GridTableRowProps
    extends ChildrenProps, React.HTMLAttributes<HTMLDivElement> {
    cells?: Array<{
        key: string;
        value: string | React.ReactNode;
        className?: string;
        onClick?: (e: MouseEvent<HTMLDivElement>) => void;
    }>;
    columnBorders?: boolean;
    className?: string;
    setNodeRef?: (node: HTMLElement | null) => void;
    clickable?: boolean;
}

/** @deprecated Use @design-system/ViewTable/ViewTable for product tables. GridTable is an internal layout primitive — do not import directly. */
export const GridTableRow: React.FC<GridTableRowProps> = React.memo(
    ({
        cells,
        columnBorders,
        className,
        children,
        setNodeRef,
        clickable,
        ...divProps
    }) => (
        <div
            ref={setNodeRef}
            {...divProps}
            className={classNames(
                styles.row,
                gridTable.row,
                {
                    [gridTable.columnBorders]: columnBorders,
                    [styles.clickable]: clickable ?? Boolean(divProps.onClick),
                },
                className,
            )}
        >
            {cells?.map((cell) => (
                <div
                    onClick={cell.onClick}
                    key={cell.key}
                    className={cell.className}
                >
                    {isFunction(cell.value) ? cell.value() : cell.value}
                </div>
            ))}
            {children}
        </div>
    ),
);
GridTableRow.displayName = "GridTableRow";
